import { api } from './api';

// Review Types
export interface BusinessReview {
  id: string;
  business_id: string;
  user_id: string;
  order_id?: string;
  rating: number;
  timely_delivery: boolean;
  sufficient_quantity: boolean;
  easy_collection: boolean;
  comment?: string;
  created_at: string;
}

export interface BusinessReviewStats {
  average_rating: number;
  total_reviews: number;
  timely_delivery_percent: number;
  sufficient_quantity_percent: number;
  easy_collection_percent: number;
  rating_distribution: {
    [key: string]: number;
  };
  recent_comments: Array<{
    id: string;
    comment: string;
    rating: number;
    created_at: string;
  }>;
}

export interface CharityReview {
  id: string;
  charity_id: string;
  user_id: string;
  donation_id?: string;
  rating: number;
  timeliness: boolean;
  politeness: boolean;
  overall_satisfaction: boolean;
  comment?: string;
  created_at: string;
}

export interface CharityReviewStats {
  average_rating: number;
  total_reviews: number;
  timeliness_percent: number;
  politeness_percent: number;
  overall_satisfaction_percent: number;
  rating_distribution: {
    [key: string]: number;
  };
  recent_comments: Array<{
    id: string;
    comment: string;
    rating: number;
    created_at: string;
  }>;
}

// Business review API functions in a nested object structure
export const business = {
  reviews: {
    async getAll(businessId: string): Promise<BusinessReview[]> {
      const response = await api.get(`/api/business-reviews?business_id=${businessId}`);
      return response.data;
    },

    async create(reviewData: Omit<BusinessReview, 'id' | 'created_at'>): Promise<BusinessReview> {
      const response = await api.post('/api/business-reviews', reviewData);
      return response.data;
    },

    async getStats(businessId: string): Promise<BusinessReviewStats> {
      const response = await api.get(`/api/business-reviews/stats/${businessId}`);
      return response.data;
    },

    async hasUserReviewed(userId: string, businessId: string, orderId?: string): Promise<boolean> {
      const queryParams = orderId ? 
        { user_id: userId, business_id: businessId, order_id: orderId } : 
        { user_id: userId, business_id: businessId };
      
      const response = await api.get('/api/business-reviews', { params: queryParams });
      return response.data.length > 0;
    }
  }
};

// Charity review API functions in a nested object structure
export const charity = {
  reviews: {
    async getAll(charityId: string): Promise<CharityReview[]> {
      const response = await api.get(`/api/charity-reviews?charity_id=${charityId}`);
      return response.data;
    },

    async create(reviewData: Omit<CharityReview, 'id' | 'created_at'>): Promise<CharityReview> {
      const response = await api.post('/api/charity-reviews', reviewData);
      return response.data;
    },

    async getStats(charityId: string): Promise<CharityReviewStats> {
      const response = await api.get(`/api/charity-reviews/stats/${charityId}`);
      return response.data;
    },

    async hasUserReviewed(userId: string, charityId: string, donationId?: string): Promise<boolean> {
      const queryParams = donationId ? 
        { user_id: userId, charity_id: charityId, donation_id: donationId } : 
        { user_id: userId, charity_id: charityId };
      
      const response = await api.get('/api/charity-reviews', { params: queryParams });
      return response.data.length > 0;
    }
  }
};

// Export combined service
export default {
  business,
  charity
};
